import { CircleAlert } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toEngineError, type EngineError } from '@/engine/errors';
import { useBatchStore } from '@/state/batch-store';
import { useEditorStore } from '@/state/editor-store';
import { selectRecordingActive, useRecordingStore } from '@/state/recording-store';
import { hasChapters, prepareFiles } from '@/state/intake';
import { startAutosave } from '@/state/projects/autosave';
import { useProjectsStore } from '@/state/projects/projects-store';
import { HomeScreen, type FileIntent } from './home/HomeScreen';
import { lazyScreen, whenIdle } from './lazy-screen';
import { Notice } from './primitives';
import { UpdateNotice } from './UpdateNotice';
import { useErrorMessage } from './use-error-message';

// The home page is all a first visit needs; the working screens follow while the browser is idle.
const editor = lazyScreen(() => import('./editor/EditorScreen').then((m) => m.EditorScreen));
const recordingScreen = lazyScreen(() => import('./recording/RecordingScreen').then((m) => m.RecordingScreen));
const batch = lazyScreen(() => import('./batch/BatchScreen').then((m) => m.BatchScreen));
const SCREENS = [editor, recordingScreen, batch];

function Screen({ screen }: { readonly screen: (typeof SCREENS)[number] }) {
  return (
    <Suspense fallback={null}>
      <screen.Screen />
    </Suspense>
  );
}

export function App() {
  const { t } = useTranslation();
  const load = useEditorStore((state) => state.load);
  const openFile = useEditorStore((state) => state.openFile);
  const openFiles = useEditorStore((state) => state.openFiles);
  const batchSize = useBatchStore((state) => state.items.length);
  const recording = useRecordingStore(selectRecordingActive);
  const message = useErrorMessage();
  const [joining, setJoining] = useState<number | null>(null);
  const [intakeError, setIntakeError] = useState<EngineError | null>(null);

  useEffect(() => whenIdle(() => SCREENS.forEach((screen) => screen.preload())), []);
  // Edits are kept on the device as they are made, so they can be picked up again later.
  useEffect(() => startAutosave({ onSaved: () => void useProjectsStore.getState().refresh() }), []);

  // One video opens the editor; with several, the first opens and the others wait in its media
  // list, ready to join or put on top (batch processing is one tap away there).
  // Quick convert (`/?to=mp4`): one video opens straight into saving it; several go to batch processing.
  // Fitting for a place (`/?fit=tiktok`) opens the editor with its frame, largest file and length check.
  // Camera chapters of one recording are joined into a single video first.
  const handleFiles = async (dropped: File[], { preset, convertTo, sizeLimit, placement, captions }: FileIntent) => {
    setIntakeError(null);
    let files = dropped;
    if (hasChapters(dropped)) {
      setJoining(0);
      try {
        files = await prepareFiles(dropped, setJoining);
      } catch (error) {
        setIntakeError(toEngineError(error));
        return;
      } finally {
        setJoining(null);
      }
    }
    if (convertTo) {
      if (files.length === 1) return void openFile(files[0]!, { convertTo, sizeLimit });
      return useBatchStore.getState().convert(files, convertTo, sizeLimit);
    }
    const options = { preset: preset ?? undefined, placement, captions };
    if (files.length === 1) return void openFile(files[0]!, options);
    void openFiles(files, options);
  };

  if (load.status === 'ready') return <Screen screen={editor} />;
  if (recording) return <Screen screen={recordingScreen} />;
  if (batchSize > 0) return <Screen screen={batch} />;
  const error = intakeError ?? (load.status === 'error' ? load.error : null);
  const busy = joining !== null || load.status === 'loading';
  return (
    <>
      <HomeScreen
        onFiles={(files, intent) => void handleFiles(files, intent)}
        loading={joining !== null ? t('load.joining', { percent: Math.round(joining * 100) }) : load.status === 'loading' ? t('load.reading') : false}
        error={
          error && (
            <Notice tone="danger" icon={<CircleAlert size={15} />}>
              {message(error)}
            </Notice>
          )
        }
      />
      {/* Only here, with nothing open: updating reloads the page. */}
      {!busy && <UpdateNotice />}
    </>
  );
}
